import { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Alert, Box, Button, Card, CardContent, Chip, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import { mutationErrorMessage } from '../../lib/mutation-error';
import PlacePicker from './PlacePicker';
import {
  EMPTY_BINDING,
  isBindingStale,
  keepBinding,
  pickedBinding,
  placeFieldsForSave,
  storedBinding,
} from '../../lib/placeBinding';
import type { GroupDetail as GroupDetailType } from '../../types/groups';
import { plural } from '../../lib/plural';

export default function RoomBlocksTab({ group, onChanged }: { group: GroupDetailType; onChanged: () => void }) {
  const [form, setForm] = useState({ hotelName: '', roomType: '', nightlyRate: '', currency: 'USD', totalRooms: '0', bookingDeadline: '', description: '', imageUrls: '', amenities: '', notes: '' });
  /*
    The pin, bound to the hotel name it was picked against.

    Unlike an itinerary tile, the bound text here is not a free label the host may
    rename — it IS the venue. And this form does not reset after a create, so a
    host adding several room types works down one long-lived form: pick Bairro
    Alto for the King room, add it, then type a different hotel over the name and
    add a Double, and the second block inherits the first hotel's coordinates.
    That is the wrong-pin failure this feature exists to stop, on a guest-facing
    row, with nothing on screen to say it happened.

    So a written block demotes its pin from 'confirmed' to 'stored'. Keeping the
    same hotel name keeps the pin (the common case is several room types at one
    hotel); changing the name makes it stale, which clears it on save and puts the
    warning and its "Keep this pin" escape in front of the host first.
  */
  const [binding, setBinding] = useState(EMPTY_BINDING);
  const roomBlockMutation = useMutation((body: any) => groupsApi.createRoomBlock(group.id, body), {
    onSuccess: (_data, variables: any) => {
      onChanged();
      setBinding((current) => storedBinding(current.place, variables?.hotelName ?? ''));
    },
  });
  const saving = roomBlockMutation.isLoading;
  const error = mutationErrorMessage(roomBlockMutation.error);
  const pinIsStale = isBindingStale(binding, form.hotelName);
  const splitLines = (v: string) => v.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} gutterBottom>Add room block</Typography>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <TextField label="Hotel" value={form.hotelName} onChange={(e) => setForm({ ...form, hotelName: e.target.value })} />
          <TextField label="Room type" value={form.roomType} onChange={(e) => setForm({ ...form, roomType: e.target.value })} />
          <TextField label="Total rooms" type="number" value={form.totalRooms} onChange={(e) => setForm({ ...form, totalRooms: e.target.value })} />
          <TextField label="Nightly rate" type="number" value={form.nightlyRate} onChange={(e) => setForm({ ...form, nightlyRate: e.target.value })} />
          <TextField label="Currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
          <TextField label="Deadline" type="date" value={form.bookingDeadline} onChange={(e) => setForm({ ...form, bookingDeadline: e.target.value })} InputLabelProps={{ shrink: true }} />

        </Box>
        {/* Replaces the latitude and longitude boxes: the hotel is findable by
            name, and the map pin follows from picking it. */}
        <Box sx={{ mt: 2 }}>
          <PlacePicker
            groupId={group.id}
            near={group.locationName}
            value={binding.place}
            stale={pinIsStale}
            onKeep={() => setBinding((current) => keepBinding(current, form.hotelName))}
            onChange={(next) => {
              setBinding(pickedBinding(next, form.hotelName || next?.name || ''));
              if (next && !form.hotelName) {
                setForm((current) => ({ ...current, hotelName: next.name || '' }));
              }
            }}
            disabled={saving}
          />
        </Box>
        <TextField fullWidth sx={{ mt: 2 }} label="Description" multiline minRows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <TextField fullWidth sx={{ mt: 2 }} label="Photo URLs (one per line or comma-separated)" multiline minRows={2} value={form.imageUrls} onChange={(e) => setForm({ ...form, imageUrls: e.target.value })} placeholder="https://…/room-1.jpg" />
        <TextField fullWidth sx={{ mt: 2 }} label="Amenities (one per line or comma-separated)" multiline minRows={2} value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} placeholder="King bed, Ocean view, Breakfast included" />
        <TextField fullWidth sx={{ mt: 2 }} label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <Button sx={{ mt: 2 }} variant="contained" disabled={saving || !form.hotelName || !form.roomType} onClick={() => roomBlockMutation.mutate({
          hotelName: form.hotelName,
          roomType: form.roomType,
          currency: form.currency,
          notes: form.notes,
          description: form.description,
          totalRooms: Number(form.totalRooms) || 0,
          nightlyRate: form.nightlyRate ? Number(form.nightlyRate) : undefined,
          bookingDeadline: form.bookingDeadline || undefined,
          imageUrls: splitLines(form.imageUrls),
          amenities: splitLines(form.amenities),
          ...placeFieldsForSave(binding, form.hotelName),
        })}>Add room block</Button>
      </Paper>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {group.roomBlocks.map((block) => (
          <Card key={block.id} sx={{ borderRadius: 3 }}>
            {block.imageUrls && block.imageUrls.length > 0 ? (
              <Box component="img" src={block.imageUrls[0]} alt={block.roomType} sx={{ width: '100%', height: 150, objectFit: 'cover' }} />
            ) : null}
            <CardContent>
              <Typography variant="caption" color="text.secondary">{block.hotelName}</Typography>
              <Typography fontWeight={800}>{block.roomType}</Typography>
              <Chip sx={{ my: 1 }} color={block.availableRooms > 0 ? 'success' : 'default'} label={`${block.availableRooms} of ${block.totalRooms} available`} />
              <Typography variant="body2" color="text.secondary">{plural(block.heldRooms, 'room')} held/invoiced/paid/booked</Typography>
              {block.latitude != null && block.longitude != null ? (
                <Typography variant="caption" color="text.secondary">{block.latitude}, {block.longitude}</Typography>
              ) : null}
              {block.imageUrls && block.imageUrls.length > 0 ? (
                <Typography variant="caption" color="text.secondary">{plural(block.imageUrls.length, 'photo')}</Typography>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </Box>
    </Stack>
  );
}
